import { zodResolver } from '@hookform/resolvers/zod';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import { useSubCategories } from 'features/sub-category/hooks/useSubCategories';
import { Camera, ListPlus, Loader, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useAddServiceCatalog } from '../hooks/useAddServiceCatalog';
import { serviceCatalogServices } from '../services/service-catalog.service';

const serviceCatalogSchema = z.object({
  mainCategory: z.string().min(1, 'Main category is required'),
  subCategory: z.string().min(1, 'Sub category is required'),
  name: z.string().min(2, 'Name must be at least 2 characters').max(150, 'Name must be at most 150 characters'),
  description: z.string().max(2000, 'Description must be at most 2000 characters').optional().or(z.literal('')),
  image: z.string().optional().or(z.literal('')),
  status: z.enum(['active', 'inactive']).optional(),
});

const AddServiceCatalogModal = (props) => {
  const { isAddModalOpen, setIsAddModalOpen } = props;
  const { data: mainCategories } = useMainCategories({ limit: 99, isActive: true });
  const { mutate: addServiceCatalog, isPending } = useAddServiceCatalog();

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(serviceCatalogSchema),
    mode: 'onChange',
    defaultValues: {
      status: 'active',
    },
  });

  const watchedMainCategory = watch('mainCategory');

  const { data: subCategories } = useSubCategories({
    parentCategory: watchedMainCategory,
    limit: 100,
  });

  useEffect(() => {
    setValue('subCategory', '');
  }, [watchedMainCategory, setValue]);

  const validateFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return false;
    }
    return true;
  };

  const handleImageSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleModalClose = () => {
    reset();
    handleRemoveImage();
    setIsAddModalOpen(false);
  };

  const onSubmit = async (data) => {
    try {
      let imageKey = '';

      if (imageFile) {
        setIsUploadingImage(true);
        const uploadResponse = await serviceCatalogServices.uploadFile(imageFile);
        imageKey = uploadResponse?.data?.key || '';
        setIsUploadingImage(false);
      }

      const formattedData = {
        ...data,
        image: imageKey || data.image || '',
      };

      addServiceCatalog(formattedData, {
        onSuccess: () => {
          reset();
          handleRemoveImage();
          setIsAddModalOpen(false);
        },
      });
    } catch (error) {
      setIsUploadingImage(false);
      toast.error(error?.response?.data?.message || 'Failed to upload file');
    }
  };

  if (!isAddModalOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>Add Service Catalog Entry</h2>
          <button onClick={handleModalClose} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>
          {/* Main Category Selection */}
          <div>
            <label className='block text-sm font-medium mb-1'>Main Category *</label>
            <select {...register('mainCategory')} className='w-full px-3 py-2 text-sm'>
              <option value=''>Select Main Category</option>
              {mainCategories?.data?.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name?.english}
                </option>
              ))}
            </select>
            {errors.mainCategory && <p className='text-destructive text-xs mt-1'>{errors.mainCategory.message}</p>}
          </div>

          {/* Sub Category Selection */}
          <div>
            <label className='block text-sm font-medium mb-1'>Sub Category *</label>
            <select {...register('subCategory')} disabled={!watchedMainCategory} className='w-full px-3 py-2 text-sm disabled:opacity-50'>
              <option value=''>Select Sub Category</option>
              {subCategories?.data?.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name?.english}
                </option>
              ))}
            </select>
            {errors.subCategory && <p className='text-destructive text-xs mt-1'>{errors.subCategory.message}</p>}
          </div>

          {/* Name */}
          <div>
            <label className='block text-sm font-medium mb-1'>Name *</label>
            <input
              type='text'
              {...register('name')}
              className='w-full px-3 py-2 text-sm'
              placeholder='e.g., Tap & Pipe Repair'
            />
            {errors.name && <p className='text-destructive text-xs mt-1'>{errors.name.message}</p>}
          </div>

          {/* Description */}
          <div>
            <label className='block text-sm font-medium mb-1'>Description</label>
            <textarea
              {...register('description')}
              rows={3}
              className='w-full px-3 py-2 text-sm'
              placeholder='Fix leaking taps and pipe joints.'
            />
            {errors.description && <p className='text-destructive text-xs mt-1'>{errors.description.message}</p>}
          </div>

          {/* Status */}
          <div>
            <label className='block text-sm font-medium mb-1'>Status</label>
            <select {...register('status')} className='w-full px-3 py-2 text-sm'>
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
            </select>
          </div>

          {/* Image Upload */}
          <div>
            <label className='block text-sm font-medium mb-2'>Image</label>
            {imagePreview ? (
              <div className='flex items-center space-x-4'>
                <img src={imagePreview} alt='Image preview' className='w-24 h-24 rounded-lg object-cover border border-border' />
                <div className='flex flex-col space-y-2'>
                  <p className='text-xs text-muted-foreground'>{imageFile?.name}</p>
                  <button
                    type='button'
                    onClick={handleRemoveImage}
                    className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                  >
                    <Trash2 size={14} />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => imageInputRef.current?.click()}
                className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
              >
                <input
                  ref={imageInputRef}
                  type='file'
                  accept='image/*'
                  onChange={(e) => handleImageSelect(e.target.files?.[0])}
                  className='hidden'
                />
                <div className='flex flex-col items-center space-y-2'>
                  <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                    <Camera size={20} className='text-primary' />
                  </div>
                  <p className='text-sm text-muted-foreground'>Click to upload image</p>
                  <p className='text-xs text-muted-foreground'>PNG, JPG up to 5MB</p>
                </div>
              </div>
            )}
            {errors.image && <p className='text-destructive text-xs mt-1'>{errors.image.message}</p>}
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={handleModalClose}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSubmitting || isPending || isUploadingImage}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
            >
              {isPending || isUploadingImage ? (
                <div className='flex items-center justify-center'>
                  <Loader size={16} className='animate-spin mr-2' />
                  {isUploadingImage ? 'Uploading...' : 'Creating...'}
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <ListPlus size={16} className='mr-2' />
                  Create Catalog Entry
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServiceCatalogModal;
